import React, { useState, useEffect, useCallback, useRef } from 'react';

/** Compile and lay out the panel during idle time without blocking the host page. */
export function usePanelWarmup(minimized: boolean) {
  // Pay the panel's one-off first-open costs (component code compiled on first call, font
  // resolution, style matching) in idle time while the launcher is closed, so the launcher
  // click only renders and lays out the panel itself. See PanelWarmup. Skipped when the
  // widget starts open or is opened before the browser goes idle.
  const [panelWarmup, setPanelWarmup] = useState<'pending' | 'active' | 'done'>(() =>
    minimized ? 'pending' : 'done'
  );
  useEffect(() => {
    if (!minimized) {
      setPanelWarmup('done');
      return undefined;
    }
    if (panelWarmup !== 'pending' || typeof window === 'undefined') return undefined;
    const start = () => {
      const activate = () => setPanelWarmup(state => (state === 'pending' ? 'active' : state));
      // A transition render is time-sliced, so the warm-up never blocks the host page.
      if (typeof React.startTransition === 'function') React.startTransition(activate);
      else activate();
    };
    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(start, { timeout: 3000 });
      return () => window.cancelIdleCallback?.(handle);
    }
    const timer = setTimeout(start, 1500);
    return () => clearTimeout(timer);
  }, [minimized, panelWarmup]);
  const finishPanelWarmup = useCallback(() => setPanelWarmup('done'), []);
  const warmupRefs = useRef<{
    end: React.RefObject<HTMLDivElement | null>;
    textArea: React.RefObject<HTMLTextAreaElement | null>;
    fileInput: React.RefObject<HTMLInputElement | null>;
  } | null>(null);
  if (!warmupRefs.current) {
    warmupRefs.current = {
      end: React.createRef<HTMLDivElement>(),
      textArea: React.createRef<HTMLTextAreaElement>(),
      fileInput: React.createRef<HTMLInputElement>(),
    };
  }

  return { panelWarmup, finishPanelWarmup, warmupRefs: warmupRefs.current };
}
